import { Box, Button, FormControl, FormLabel, TextField } from '@mui/material';
import { useEffect } from 'react';
import { CommonDialog, CommonDialogHeader } from '..';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { IPermissionsContent, IRootState } from '../../../types/store';
import { IUpdatePermissionRequest } from '../../../types/api/request/permissionsrequest';
import { PermissionsService } from '../../../api/services/permissionsService';
import { updatePermission } from '../../../store/Reducers/PermissionsSlice';
import { showNotification } from '../../../store/Reducers/NotificationSnackbarSlice';

interface Props {
  selectedPermission: IPermissionsContent | null;
  isOpen: boolean;
  onClose: () => void;
}

export const EditPermissionDialog = ({ selectedPermission, isOpen, onClose }: Props) => {
  const dispatch = useDispatch();

  const onSubmit = async (payload: IUpdatePermissionRequest) => {
    try {
      const updated = await PermissionsService.updatePermission(payload);
      dispatch(updatePermission(updated));
      dispatch(showNotification({ message: 'Permission successfully updated.' }));
      onClose();
    } catch (error: any) {
      dispatch(
        showNotification({
          message: `Failed to update permission. ${error?.response?.data?.message || ''}`,
          severity: 'error',
        }),
      );
    }
  };

  if (!selectedPermission) return null;

  return (
    <CommonDialog
      isOpen={isOpen}
      header={<CommonDialogHeader title="Edit Permission" onClose={onClose} />}
      body={
        <DialogBody onSubmit={onSubmit} selectedPermission={selectedPermission} isOpen={isOpen} />
      }
      footer={undefined}
    />
  );
};

interface BodyProps {
  onSubmit: (payload: IUpdatePermissionRequest) => Promise<void>;
  selectedPermission: IPermissionsContent;
  isOpen: boolean;
}

const DialogBody = ({ onSubmit, selectedPermission, isOpen }: BodyProps) => {
  const { content: permissions } = useSelector((state: IRootState) => state.permissions);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<IUpdatePermissionRequest>({
    defaultValues: {
      id: selectedPermission.id,
      name: selectedPermission.name,
      description: selectedPermission.description ?? '',
    },
  });

  useEffect(() => {
    reset({
      id: selectedPermission.id,
      name: selectedPermission.name,
      description: selectedPermission.description ?? '',
    });
  }, [selectedPermission.id, isOpen]);

  const handleFormSubmit = async (data: IUpdatePermissionRequest) => {
    try {
      await onSubmit(data);
      reset();
    } catch {}
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(handleFormSubmit)}
      noValidate
      sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2 }}
    >
      <FormControl>
        <FormLabel>Name</FormLabel>
        <TextField
          type="text"
          fullWidth
          variant="outlined"
          error={!!errors.name}
          helperText={errors.name?.message}
          {...register('name', {
            required: 'Permission name is required',
            validate: (value) => {
              const taken = permissions?.some(
                (p) =>
                  p.id !== selectedPermission.id &&
                  p.name.toLowerCase() === value?.trim().toLowerCase(),
              );
              return taken ? 'A permission with this name already exists' : true;
            },
          })}
        />
      </FormControl>

      <FormControl>
        <FormLabel>Description</FormLabel>
        <TextField
          type="text"
          fullWidth
          variant="outlined"
          error={!!errors.description}
          helperText={errors.description?.message}
          {...register('description', {
            required: 'Description is required',
          })}
        />
      </FormControl>

      <Button
        type="submit"
        variant="contained"
        disabled={isSubmitting}
        sx={{ backgroundColor: '#000' }}
      >
        {isSubmitting ? 'Updating...' : 'Update Permission'}
      </Button>
    </Box>
  );
};
